import { useEffect, useMemo, useRef, useState } from "react";

import AssignmentIcon from "@mui/icons-material/Assignment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import Lottie from "react-lottie";
import { Bar } from "../../../../theme/charts";
import useResizeObserver from "use-resize-observer";

import { assets } from "../../../../assets";
import { EBenefitTypes } from "../../../../permissions/benefits/enums/benefits.enums";
import { widgetSmallHeight } from "../../../../utils";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { WidgetComponentCharts } from "../../../common/WidgetComponentCharts";
import { outLinedButton } from "../../../ui/styles/buttonStyles";
import { SASSMenuPropsStyles, SASSelectMenuStyles } from "../../../ui/styles/inputStyles";
import { STATUS_COLORS, TASK_STATUSES } from "../../constants";
import { useKanbanData } from "../../hooks/useKanbanData";
import { FlowDoMainLayout } from "../FlowDoMainLayout/FlowDoMainLayout";
import { ToggleModeSwitch } from "./components/ToggleModeSwitch";
import { useMediaQuery } from "@mui/material";
import { RiveByKey } from "@components/riveAnimation";

import { useBenefitStrategy } from "../../../../permissions/access";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STATUS_LABELS = {
  TODO: "To-do",
  IN_PROGRESS: "In progress",
  DONE: "Done",
};

const LOTTIE_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData: assets.animation.taskLoader,
  rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
};

const buildChartOptions = (selectedPage, selectedCategory, totalTasks) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: selectedCategory ? `Tasks in ${selectedCategory.name}: ${totalTasks}` : `Total tasks in ${selectedPage?.name || "Board"}: ${totalTasks}`,
    },
    tooltip: {
      callbacks: { label: (ctx) => ` ${ctx.raw} tasks` },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12, weight: 600 } } },
    y: { grid: { display: false }, beginAtZero: true, ticks: { stepSize: 1 } },
  },
});

const buildChartData = (tasks) => {
  const taskCountsByStatus = TASK_STATUSES.reduce((acc, status) => {
    acc[status] = tasks.filter((task) => task.status === status).length;
    return acc;
  }, {});

  return {
    labels: TASK_STATUSES.map((status) => STATUS_LABELS[status] ?? status),
    datasets: [
      {
        label: "Tasks",
        data: TASK_STATUSES.map((status) => taskCountsByStatus[status] || 0),
        backgroundColor: TASK_STATUSES.map((status) => STATUS_COLORS[status]?.bg || "var(--fx-surface-muted, #F1F5F9)"),
        borderColor: TASK_STATUSES.map((status) => STATUS_COLORS[status]?.border || "var(--fx-surface-texttertiary, #94A3B8)"),
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };
};

export const FlowDoWidget = ({ swapyName, ...props }) => {
  const access = useBenefitStrategy(EBenefitTypes.FLOW_DO);

  const { ref, height = 0 } = useResizeObserver();
  const openModalRef = useRef(null);

  const isSmallScreen = useMediaQuery("(max-width: 600px)");

  const { pages: kanbanPages, isLoading: isLoadingPages } = useKanbanData();

  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const [viewMode, setViewMode] = useState("graph");

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const showSmallDescription = height < 250;

  const selectedPage = useMemo(() => kanbanPages.find((p) => p.uid === selectedBoardId), [kanbanPages, selectedBoardId]);

  const categories = selectedPage?.categories || [];

  const selectedCategory = useMemo(() => categories.find((c) => c.uid === selectedCategoryId), [categories, selectedCategoryId]);

  const tasks = useMemo(() => {
    if (selectedCategory) return selectedCategory?.tasks ?? [];
    if (selectedPage) return (selectedPage?.categories ?? []).flatMap((c) => c?.tasks ?? []);
    return [];
  }, [selectedPage, selectedCategory]);

  useEffect(() => {
    if (!selectedBoardId && kanbanPages?.length) {
      setSelectedBoardId(kanbanPages[0].uid);
    }
  }, [kanbanPages, selectedBoardId]);

  useEffect(() => {
    setSelectedCategoryId("");
  }, [selectedBoardId]);

  const openModal = (preview) => {
    setIsPreviewMode(preview);
    openModalRef.current?.click();
  };

  const totalTasks = tasks.length;
  const chartData = useMemo(() => buildChartData(tasks), [tasks]);
  const chartOptions = useMemo(() => buildChartOptions(selectedPage, selectedCategory, totalTasks), [selectedPage, selectedCategory, totalTasks]);

  return (
    <>
      <WidgetComponentCharts
        sxProp={{ height: widgetSmallHeight }}
        refResizeObserver={ref}
        swapyName={swapyName || "flow-do"}
        title="Flow-DO"
        description={showSmallDescription ? "Task management" : "Organize and track your tasks with Kanban boards and lists."}
        icon={<AssignmentIcon />}
        data={
          <ChartContent
            kanbanPages={kanbanPages}
            categories={categories}
            selectedBoardId={selectedBoardId}
            selectedCategoryId={selectedCategoryId}
            isLoadingPages={isLoadingPages}
            totalTasks={totalTasks}
            chartData={chartData}
            chartOptions={chartOptions}
            onBoardChange={setSelectedBoardId}
            onCategoryChange={setSelectedCategoryId}
            viewMode={viewMode}
            tasks={tasks}
          />
        }
        actions={
          <Actions
            openModalRef={openModalRef}
            onOpen={openModal}
            isPreviewMode={isPreviewMode}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            isSmallScreen={isSmallScreen}
            access={access}
          />
        }
      />
    </>
  );
};

const ChartContent = ({
  kanbanPages,
  categories,
  selectedBoardId,
  selectedCategoryId,
  isLoadingPages,
  totalTasks,
  chartData,
  chartOptions,
  onBoardChange,
  onCategoryChange,
  viewMode,
  tasks,
}) => (
  <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1, marginTop: -2 }}>
    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
      {kanbanPages?.length > 0 && (
        <FormControl size="small" sx={{ flex: 1, backgroundColor: "var(--fx-surface-base, #FFFFFF)" }}>
          <Select
            value={selectedBoardId}
            onChange={(e) => onBoardChange(e.target.value)}
            sx={{
              ...SASSelectMenuStyles,
              minWidth: 0,
              "& .MuiSelect-select": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
            MenuProps={{
              ...SASSMenuPropsStyles,
              PaperProps: {
                ...SASSMenuPropsStyles?.PaperProps,
                sx: {
                  ...SASSMenuPropsStyles?.PaperProps?.sx,
                  width: 260,
                  maxHeight: 300,
                  overflowX: "hidden",
                  overflowY: "auto",
                },
              },
            }}
          >
            {kanbanPages.map((page) => (
              <MenuItem
                key={page.uid}
                value={page.uid}
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {page.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {categories.length > 0 && (
        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={selectedCategoryId}
            onChange={(e) => onCategoryChange(e.target.value)}
            displayEmpty
            renderValue={(value) => {
              if (!value) return "All Categories";
              return categories.find((cat) => cat.uid === value)?.name ?? "All Categories";
            }}
            sx={{
              ...SASSelectMenuStyles,
              minWidth: 0,
              "& .MuiSelect-select": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
            MenuProps={{
              ...SASSMenuPropsStyles,
              PaperProps: {
                ...SASSMenuPropsStyles?.PaperProps,
                sx: {
                  ...SASSMenuPropsStyles?.PaperProps?.sx,
                  width: 260,
                  maxHeight: 300,
                  overflowX: "hidden",
                  overflowY: "auto",
                },
              },
            }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem
                key={cat.uid}
                value={cat.uid}
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>

    {isLoadingPages ? (
      <Box sx={{ p: 2, textAlign: "center", color: "var(--fx-surface-texttertiary, #94A3B8)" }}>Loading...</Box>
    ) : totalTasks === 0 ? (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{}}>
          {/* <Lottie options={lottieOptions} height={100} width={100} isClickToPauseDisabled /> */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              marginTop: 0,
            }}
          >
            <RiveByKey riveKey="timesheets" boxSize={200} />
          </Box>
          <Typography
            variant="body2"
            sx={{ display: "flex", mt: -2, color: "text.secondary", textAlign: "center", fontSize: "0.85rem", opacity: 0.8 }}
          >
            No tasks found
          </Typography>
        </Box>
      </Box>
    ) : viewMode === "graph" ? (
      <Box sx={{ height: 220, mt: 1 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>
    ) : (
      <Box sx={{ height: 220, mt: 1, overflowY: "auto", pr: 1 }}>
        {(tasks ?? []).map((task) => (
          <Box
            key={task?.uid ?? ""}
            sx={{
              p: 1,
              mb: 1,
              borderRadius: "8px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {task?.name ?? ""}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: "4px",
                bgcolor: STATUS_COLORS[task?.status ?? ""]?.bg || "var(--fx-surface-muted, #F1F5F9)",
                color: STATUS_COLORS[task?.status ?? ""]?.color || "var(--fx-surface-textsecondary, #64748B)",
              }}
            >
              {STATUS_LABELS[task?.status ?? ""] || task?.status || ""}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

const Actions = ({ openModalRef, onOpen, viewMode, onViewModeChange, isSmallScreen, access }) => (
  <Box sx={{ display: "flex", flexDirection: "row-reverse", gap: 2 }}>
    <ToggleModeSwitch mode={viewMode} onViewModeChange={onViewModeChange} />

    {/* <BenefitPreviewGate
      benefit={EBenefitTypes.FLOW_DO}
      featureName="Flow Do"
      onPreview={() => onOpen(true)}
    > */}

    {/* <AccessGate
      strategy={access}
      behavior={PreviewBehavior}
      behaviorConfig={{
        featureName: "Flow Do",
        onPreview: () => onOpen(true),
      }}
    > */}
    <Button sx={outLinedButton} startIcon={<VisibilityIcon />} onClick={() => onOpen(false)}>
      View
    </Button>
    {/* </AccessGate> */}

    {/* </BenefitPreviewGate> */}

    <BaseModalComponent
      modalLabel="Flow-DO"
      modalKey="Dashboard-FlowDo-Business"
      defaultSizes={{
        width: { xs: "95%", sm: "90%", md: "1600px" },
        height: { xs: "95%", sm: "90%", md: "90%" },
      }}
      elementTrigger={<button ref={openModalRef} type="button" style={{ display: "none" }} />}
    >
      <FlowDoMainLayout isPreviewMode={false} />
    </BaseModalComponent>
  </Box>
);
